<!-- CUSTOM: bocket-evolution-signature-toggle — BKT-015 -->
<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Switch from 'next/switch/Switch.vue';
import BocketEvolutionSignatureAPI from 'dashboard/api/bocket/evolutionSignature';

const { t } = useI18n();

const isAvailable = ref(false);
const isLoading = ref(true);
const isSubmitting = ref(false);
const senderNameEnabled = ref(false);

const fetchStatus = async () => {
  try {
    const response = await BocketEvolutionSignatureAPI.get();
    isAvailable.value = response.data.available;
    senderNameEnabled.value = !!response.data.sender_name_enabled;
  } catch (error) {
    isAvailable.value = false;
  } finally {
    isLoading.value = false;
  }
};

// One-way `:model-value` binding on purpose (no v-model): this component is
// the single owner of `senderNameEnabled`, computing the flip itself and
// reverting on failure. Relying on Switch.vue's own emitted values here
// raced against its internal state and made error-revert unreliable.
const toggle = async () => {
  const previousValue = senderNameEnabled.value;
  const newValue = !previousValue;
  senderNameEnabled.value = newValue;
  isSubmitting.value = true;
  try {
    const response = await BocketEvolutionSignatureAPI.update(newValue);
    senderNameEnabled.value = !!response.data.sender_name_enabled;
    useAlert(t('GENERAL_SETTINGS.FORM.EVOLUTION_SIGNATURE.API.SUCCESS'));
  } catch (error) {
    senderNameEnabled.value = previousValue;
    useAlert(t('GENERAL_SETTINGS.FORM.EVOLUTION_SIGNATURE.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(fetchStatus);
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="!isLoading && isAvailable"
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <div class="flex justify-between items-center w-full">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ t('GENERAL_SETTINGS.FORM.EVOLUTION_SIGNATURE.TITLE') }}
        </h3>
        <div class="flex justify-end">
          <Switch
            :model-value="senderNameEnabled"
            :disabled="isSubmitting"
            @change="toggle"
          />
        </div>
      </div>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('GENERAL_SETTINGS.FORM.EVOLUTION_SIGNATURE.NOTE') }}
      </p>
    </div>
  </div>
</template>
